import axios from 'axios';
import { url } from '../env';
import { buildFunnelSteps } from './funnelSteps';

const PASO_TO_STATUS = {
    'Faltan documentos básicos':   { code: 'docs_pending', label: 'Faltan documentos' },
    'Análisis':                    { code: 'analysis',     label: 'Análisis' },
    'En proceso visita ocular':    { code: 'ocular',       label: 'Visita ocular' },
    'En proceso cotización':       { code: 'quoting',      label: 'En cotización' },
    'Pago inicial':                { code: 'paid',         label: 'Pago inicial' },
    'Orden de compra':             { code: 'invoiced',     label: 'Orden de compra' },
    'Contrato':                    { code: 'contract_step', label: 'Contrato' },
    'Seguros':                     { code: 'insurance',    label: 'Seguros' },
    'Placas':                      { code: 'plates',       label: 'Placas' },
    'Rechazado':                   { code: 'rejected',     label: 'Rechazado' },
    'Aprobado':                    { code: 'approved',     label: 'Aprobado' },
};

const STEP_LABEL_VERB = {
    ocular_visit:         'Visita ocular',
    confirm_quote:        'Cotización',
    upload_payment_proof: 'Pago inicial',
    request_invoice:      'Orden de compra',
    contract:             'Contrato',
    add_insurance:        'Seguros',
    register_plates:      'Placas',
};

const buildActionDetail = (statusCode, currentStepType, currentStepStatus, currentStepCompletedAt, missingDocs) => {
    const fecha = formatDate(currentStepCompletedAt);

    if (statusCode === 'docs_pending') {
        const n = Array.isArray(missingDocs) ? missingDocs.length : 0;
        return n > 0 ? `${n} documento(s) pendiente(s)` : 'Documentación incompleta';
    }
    if (statusCode === 'rejected') {
        const verb = STEP_LABEL_VERB[currentStepType] ?? 'el proceso';
        return `Rechazado en ${verb.toLowerCase()}`;
    }
    if (statusCode === 'approved') {
        return fecha ? `Crédito aprobado el ${fecha}` : 'Crédito aprobado';
    }
    if (statusCode === 'contract_step') {
        if (currentStepStatus === 'completed') {
            return fecha ? `Contrato finalizado el ${fecha} · pasando a seguros` : 'Contrato finalizado';
        }
        return 'Contrato en proceso';
    }
    if (statusCode === 'insurance') {
        if (currentStepStatus === 'completed') {
            return fecha ? `Seguro registrado el ${fecha}` : 'Seguro registrado';
        }
        return 'Registrando seguro';
    }
    if (statusCode === 'plates') {
        if (currentStepStatus === 'completed') {
            return fecha ? `Placas registradas el ${fecha}` : 'Placas registradas';
        }
        return 'Tramitando placas';
    }

    switch (currentStepType) {
        case 'ocular_visit':
            if (currentStepStatus === 'scheduled')   return 'Visita agendada — pendiente';
            if (currentStepStatus === 'postponed')   return 'Visita reprogramada';
            if (currentStepStatus === 'in-progress') return 'Visita en curso';
            return 'Pendiente de agendar visita';
        case 'confirm_quote':
            if (currentStepStatus === 'completed') {
                return fecha
                    ? `Cotización enviada el ${fecha} · esperando confirmación`
                    : 'Cotización enviada · esperando confirmación';
            }
            if (currentStepStatus === 'in-progress') return 'Cotización en preparación';
            return 'Pendiente de cotizar';
        case 'upload_payment_proof':
            if (currentStepStatus === 'completed') {
                return fecha
                    ? `Pago confirmado el ${fecha} · esperando factura`
                    : 'Pago confirmado · esperando factura';
            }
            if (currentStepStatus === 'in-progress') return 'Pago parcial recibido';
            return 'Esperando comprobante de pago';
        case 'request_invoice':
            if (currentStepStatus === 'completed') {
                return fecha
                    ? `Factura recibida el ${fecha} · en revisión`
                    : 'Factura recibida · en revisión';
            }
            if (currentStepStatus === 'in-progress') return 'Solicitando factura al proveedor';
            return 'Esperando factura';
        default:
            if (statusCode === 'analysis') return 'Revisión inicial pendiente';
            return null;
    }
};

const formatDate = (iso) => {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return null;
        return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
        return null;
    }
};

const buildUnitName = (info) => {
    const src = info?.car ?? info?.vehicle;
    if (!src) return null;
    const { brand, model, year } = src;
    return [brand, model, year].filter(Boolean).join(' ');
};

const adaptUserToRow = (u) => {
    const paso = u.paso_actual ?? 'Análisis';
    const map = PASO_TO_STATUS[paso] ?? { code: 'analysis', label: paso };
    return {
        id: u.id,
        user_id: u.id,
        budget_id: u.infoBudget?.budget_id ?? null,
        is_hidden: !!(u.is_hidden ?? u.infoBudget?.is_hidden),
        is_rejected: !!(u.is_rejected ?? u.infoBudget?.is_rejected),
        has_quote_process: !!u.has_quote_process,
        client: {
            id:    u.id,
            name:  u.name_syntage || u.name || null,
            email: u.email ?? null,
            phone: u.phone ?? null,
        },
        advisor: u.advisor ?? null,
        creator: u.creator ?? null,
        unit: buildUnitName(u.infoBudget),
        status: map.code,
        status_label: map.label,
        action_text: paso,
        price: u.infoBudget?.price ?? null,
        monthly_rent: u.infoBudget?.monthly_rent ?? null,
        term: u.infoBudget?.term ?? null,
        source: u.source ?? u.infoBudget?.source ?? null,
        insurance_effective_status: u.insurance_effective_status ?? null,
        insurance_start_date: u.insurance_start_date ?? null,
        steps: buildFunnelSteps({
            statusCode: map.code,
            currentStepType: u.current_step_type ?? null,
            currentStepStatus: u.current_step_status ?? null,
            currentStepCompletedAt: u.current_step_completed_at ?? null,
            rejectedAtStep: u.rejected_at_step ?? null,
            docsHasWarnings: Array.isArray(u.docs_warnings) && u.docs_warnings.length > 0,
            insuranceEffectiveStatus: u.insurance_effective_status ?? null,
            insuranceStartDate: u.insurance_start_date ?? null,
        }),
        action_detail: u.action_detail ?? buildActionDetail(
            map.code,
            u.current_step_type ?? null,
            u.current_step_status ?? null,
            u.current_step_completed_at ?? null,
            u.missing_docs ?? null,
        ),
        created_at: u.fecha_generacion ?? null,
        plan_tipo: u.plan_tipo ?? null,
        persona_subtype: u.persona_subtype ?? null,
        tipo_persona: u.tipo_persona ?? null,
        creditrust_registered: !!u.creditrust_registered,
        syntage_status: !!u.syntage_status,
        buro_status:    !!u.buro_status,
        has_requote:    !!u.has_requote,
        requote_at:     u.requote_at ?? null,
        _raw: u,
    };
};

export async function fetchCotizacionesBuro(
    {
        page = 1,
        perPage = 15,
        search = '',
        tipoPersona = 'all',
        status = null,
        advisorId = null,
        source = null,
    } = {},
    { signal } = {},
) {
    const token = localStorage.getItem('token');
    const params = {
        page,
        per_page: perPage,
        search,
        tipo_persona: tipoPersona,
    };
    if (status) params.status = status;
    if (advisorId) params.advisor_id = advisorId;
    if (source) params.source = source;

    const response = await axios.get(`${url}/getUsersReport`, {
        signal,
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        params,
    });
    const raw = response.data;
    const items = Array.isArray(raw?.data) ? raw.data : [];
    const rows  = items.map(adaptUserToRow);

    return {
        rows,
        meta: {
            current_page: raw?.pagination?.current_page ?? 1,
            last_page:    raw?.pagination?.last_page    ?? 1,
            per_page:     raw?.pagination?.per_page     ?? perPage,
            total:        raw?.pagination?.total        ?? rows.length,
        },
        kpis: raw?.kpis ?? {},
        vendors: Array.isArray(raw?.vendors) ? raw.vendors : [],
    };
}
